import React from 'react';

import PropTypes from 'prop-types';

import { CameraIcon } from '@iris/react-icons/CameraIcon';
import { InformationCircleIcon } from '@iris/react-icons/InformationCircleIcon';

import { useDevice } from '@bonnet/next/device';

import { Button, Carousel, Image, LinkIcon, ResponsiveImage } from 'reaxl';
import { DelayedImpressionObserver } from 'reaxl-analytics';
import { EmailCTA } from 'reaxl-email';

import { cardImages } from '@/containers/vdp/PurchaseWithConfidenceContainer/staticContent';

import getSliderMedia from './getSliderMedia';
import MediaGalleryImage from './MediaGalleryImage';
import MediaSliderShowcase from './MediaSliderShowcase';

function MediaSlider({
    images = [],
    videos = [],
    listingTitle = 'Listing',
    emailCTAProps,
    onImageClick,
    onVideoClick,
    onPsxRequestButtonClick,
    onVhrClick,
    onVhrImpression,
    isPsxVdp = false,
    showSpin = false,
    hasFyusion = false,
    mediaType = 'md',
    vhrTile,
}) {
    const device = useDevice();

    const isSm = device.is.sm;
    const isMobileOrTablet = device.lessThan.md;

    const fallbackImg = '/cm-api/content/static/img/placeholders/vin_placeholder.png';

    const mediaArray = getSliderMedia({ images, videos, isPsxVdp, showSpin, vhrTile, fallbackImg });

    let carouselProps = {
        fullWidthScrollXs: true,
        initialIndex: 0,
        scrollItemsCount: 3, // 6 photos at a time
        visibilityThreshold: 0.9,
        'data-cmp': 'mediaSlider',
    };

    // Render 360 spin showcase
    const renderSpinShowcase = (data, key = 'spin-360') => (
        <MediaSliderShowcase
            key={key}
            type="spin"
            src={data.src}
            alt={`${listingTitle} 360° Tour`}
            mediaType={mediaType}
            hasFyusion={hasFyusion}
            listingTitle={listingTitle}
        />
    );

    // Render video showcase
    const renderVideoShowcase = (data, videoIndex, key) => (
        <MediaSliderShowcase
            key={key}
            type="video"
            src={images[videoIndex]?.src || images[0]?.src}
            videoThumbnail={data.thumbnail}
            alt={`${listingTitle} video ${videoIndex + 1}`}
            mediaType={mediaType}
            listingTitle={listingTitle}
            videoIndex={videoIndex}
            onClick={onVideoClick}
        />
    );

    // Render image
    const renderImageComponent = (data, imageIndex, key) => {
        const isFirstImageInArray = imageIndex === 0;

        return (
            <MediaGalleryImage
                alt={`${listingTitle} image ${imageIndex + 1}`}
                isSlider
                key={key}
                lazyImage={isFirstImageInArray ? 'eager' : 'lazy'}
                fetchPriority={isFirstImageInArray ? 'high' : 'auto'}
                onClick={(e) => onImageClick(e, imageIndex)}
                src={data.src}
                type="main"
            />
        );
    };

    // Render AutoCheck VHR tile
    const renderAutoCheckVhr = () => (
        <DelayedImpressionObserver
            key="vhr-tile-impression"
            onChange={onVhrImpression}
            delay={0}
            id="vhr-tile-impression"
        >
            <div
                className="slider-button img-rounded flex-column"
                key="AutoCheckVhr"
            >
                <Image
                    src={cardImages.EXPERIAN_KBB}
                    alt="Media Slider Autocheck VHR image"
                    height="72px"
                    lazyload
                    loading="lazy"
                    draggable={false}
                />
                <LinkIcon
                    className="margin-top-2"
                    onClick={onVhrClick}
                    rel="nofollow noopener"
                    target="_blank"
                    undecorated
                >
                    View History Report
                </LinkIcon>
            </div>
        </DelayedImpressionObserver>
    );

    // Render request button
    const renderRequestButton = (labelKey = 'morePhotos') => {
        if (isPsxVdp && onPsxRequestButtonClick) {
            return (
                <div
                    key="RequestButton"
                    className="slider-button img-rounded"
                >
                    <Button
                        bsStyle="text"
                        className="display-flex flex-column"
                        onClick={onPsxRequestButtonClick}
                        aria-label={`Request more ${labelKey === 'moreInfo' ? 'information' : 'photos'} from seller`}
                    >
                        {labelKey === 'moreInfo' ? (
                            <InformationCircleIcon className="iris-icon margin-bottom-2" />
                        ) : (
                            <CameraIcon className="iris-icon margin-bottom-2" />
                        )}
                        Request More
                        <br />
                        {labelKey === 'moreInfo' ? 'Info' : 'Photos'}
                    </Button>
                </div>
            );
        }

        return (
            <EmailCTA
                {...emailCTAProps}
                className="slider-button img-rounded"
                key="requestButton"
                showIcon={false}
                buttonStyle="link"
                context={labelKey}
                labelKey={labelKey}
            />
        );
    };

    // Render placeholder image
    const renderImgPlaceholder = () => (
        <div
            className="position-relative"
            key="ImgPlaceholder"
        >
            <ResponsiveImage
                alt={`${listingTitle} image placeholder`}
                src={fallbackImg}
                lazyload={false}
                draggable={false}
            />
            <div className="placeholder-box-shadow img-rounded" />
        </div>
    );

    // Render all media types
    const renderMediaByType = (media, customKey = null) => {
        const { type, data, index, videoIndex, imageIndex } = media;
        const key = customKey || `${type}-${index}`;

        switch (type) {
            case 'spin':
                return renderSpinShowcase(data, key);
            case 'video':
                return renderVideoShowcase(data, videoIndex, key);
            case 'image':
                return renderImageComponent(data, imageIndex, key);
            case 'vhr':
                return renderAutoCheckVhr();
            case 'placeholder':
                // Don't show placeholder on mobile when user has limited images
                return !isMobileOrTablet ? renderImgPlaceholder() : null;
            case 'requestButton':
                return renderRequestButton(images?.length >= 10 ? 'moreInfo' : 'morePhotos');
            default:
                return null;
        }
    };

    // Xs and Sm Slider layout
    if (isMobileOrTablet) {
        carouselProps = {
            ...carouselProps,
            className: !isSm ? 'row' : '',
            prevNextButtons: false,
            singleImageMode: true,
            scrollItemsCount: 1,
        };

        return (
            <Carousel
                {...carouselProps}
            >
                {mediaArray.map((media) => renderMediaByType(media))}
            </Carousel>
        );
    }

    // Md and Lg Slider layout
    const [firstMedia, ...restMedia] = mediaArray || [];

    // Group rest elements into pairs for two-column layout
    const columns = [];
    for (let i = 0; i < restMedia.length; i += 2) {
        columns.push(restMedia.slice(i, i + 2));
    }

    // Render the first media item (takes up two column spaces)
    const renderFirstMedia = () => {
        if (!firstMedia) return null;
        return renderMediaByType(firstMedia);
    };

    return (
        <Carousel
            {...carouselProps}
        >
            {firstMedia && (
                <div key="First-Media">
                    {renderFirstMedia()}
                </div>
            )}
            {/* FOR 6 PHOTOS SCROLLING: Spacer div to occupy one column, so that firstMedia visually takes up the space of two columns */}
            {firstMedia && <div key="First-Media-Spacer" />}
            {columns.map((pair, colIdx) => (
                <div
                    key={'Column ' + colIdx}
                    className="media-slider-column"
                >
                    {pair.map((element, idx) => (
                        <div
                            key={`Column-${colIdx}-Media-${idx}`}
                            className="padding-1"
                        >
                            {renderMediaByType(element, `${element.type}-${element.index}`)}
                        </div>
                    ))}
                </div>
            ))}
        </Carousel>
    );
}

MediaSlider.propTypes = {
    images: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.string.isRequired,
        })
    ).isRequired,
    videos: PropTypes.arrayOf(
        PropTypes.shape({
            thumbnail: PropTypes.string,
            videoId: PropTypes.string,
        })
    ),
    listingTitle: PropTypes.string,
    emailCTAProps: PropTypes.object,
    onImageClick: PropTypes.func.isRequired,
    onVideoClick: PropTypes.func,
    onPsxRequestButtonClick: PropTypes.func,
    onVhrClick: PropTypes.func,
    onVhrImpression: PropTypes.func,
    isPsxVdp: PropTypes.bool,
    showSpin: PropTypes.bool,
    hasFyusion: PropTypes.bool,
    mediaType: PropTypes.string,
    vhrTile: PropTypes.object,
};

export default React.memo(MediaSlider);
